import {
    type OpenApiSchemaPriceIndexSignal,
    type OpenApiSchemaProductVariantRequestFilter,
    type OpenApiSchemaProductVariantRequestWithoutPagination,
} from '#shared/api/schema';
import { isAbcCombinedCategory } from '#shared/lib/abcAnalysis';
import { DISCOUNT_FILTER_ID } from '#shared/lib/filters';
import {
    HAS_GROUPING_CHARS_ID,
    HAS_MEDIA_FILTER_ID,
    HAS_MINIMAL_PRICE_FILTER_ID,
    PRICE_INDEX_CHEAPEST_MARKETPLACE_FILTER_ID,
    PRICE_INDEX_COUNTS_FILTER_ID,
    PRICE_INDEX_DIFFERENCE_FILTER_ID,
    PRICE_INDEX_PERCENT_FILTER_ID,
    REQUIRES_MARKING_FILTER_ID,
    WAREHOUSES_FILTER_ID,
} from '../constants';
import { ABC_CATEGORIES_FILTER_ID } from '../lib/abcAnalysis';
import { isHasMinimalPriceFilterId } from '../lib/filters';
import { isPriceIndexMarketplace } from '../lib/filters/createPriceIndexCheapestMarketplaceFilter';
import { isPriceIndexSignal } from '../lib/filters/createPriceIndexSignalsFilter';

const SPECIAL_FILTER_IDS = [
    ABC_CATEGORIES_FILTER_ID,
    HAS_MEDIA_FILTER_ID,
    REQUIRES_MARKING_FILTER_ID,
    WAREHOUSES_FILTER_ID,
    DISCOUNT_FILTER_ID,
    HAS_GROUPING_CHARS_ID,
    PRICE_INDEX_CHEAPEST_MARKETPLACE_FILTER_ID,
    PRICE_INDEX_COUNTS_FILTER_ID,
    PRICE_INDEX_PERCENT_FILTER_ID,
    PRICE_INDEX_DIFFERENCE_FILTER_ID,
    HAS_MINIMAL_PRICE_FILTER_ID,
];

type ProductVariantFiltersRequest = Pick<
    OpenApiSchemaProductVariantRequestWithoutPagination,
    | 'abc_categories'
    | 'filter'
    | 'has_media'
    | 'requires_marking'
    | 'has_grouping_characteristics'
    | 'warehouses'
    | 'discount_ids'
    | 'price_index_cheapest_marketplace'
    | 'price_index_signals'
    | 'price_index_percent'
    | 'price_index_diff_size'
    | 'minimal_price'
>;

export function mapProductVariantFiltersToRequest(
    filters: OpenApiSchemaProductVariantRequestFilter[] | undefined
): ProductVariantFiltersRequest {
    const filtersByField = new Map<string, OpenApiSchemaProductVariantRequestFilter>();

    filters?.forEach((filter) => {
        if (!filtersByField.has(filter.field)) {
            filtersByField.set(filter.field, filter);
        }
    });

    const abcCategories = filtersByField.get(ABC_CATEGORIES_FILTER_ID)?.values?.filter(isAbcCombinedCategory);
    const hasMedia = filtersByField.get(HAS_MEDIA_FILTER_ID)?.value;
    const requiresMarking = filtersByField.get(REQUIRES_MARKING_FILTER_ID)?.value;
    const hasGroupingCharacteristics = filtersByField.get(HAS_GROUPING_CHARS_ID)?.value;
    const warehouses = filtersByField.get(WAREHOUSES_FILTER_ID)?.values;
    const discountIds = filtersByField.get(DISCOUNT_FILTER_ID)?.values;
    const priceIndexCheapestMarketplace = filtersByField.get(PRICE_INDEX_CHEAPEST_MARKETPLACE_FILTER_ID)?.values?.at(0);
    const priceIndexCounts = filtersByField.get(PRICE_INDEX_COUNTS_FILTER_ID)?.values;
    const priceIndexPercent = filtersByField.get(PRICE_INDEX_PERCENT_FILTER_ID);
    const priceIndexDiffSize = filtersByField.get(PRICE_INDEX_DIFFERENCE_FILTER_ID);
    const [minimalPrice] = filtersByField.get(HAS_MINIMAL_PRICE_FILTER_ID)?.values ?? [];

    return {
        abc_categories: abcCategories?.length ? abcCategories : undefined,
        filter: filters?.filter((filter) => !SPECIAL_FILTER_IDS.includes(filter.field)),
        has_media: hasMedia ? hasMedia === 'true' : undefined,
        requires_marking: requiresMarking ? requiresMarking === 'true' : undefined,
        has_grouping_characteristics: hasGroupingCharacteristics ? hasGroupingCharacteristics === 'true' : undefined,
        warehouses,
        discount_ids: discountIds?.length ? discountIds : undefined,
        price_index_cheapest_marketplace: isPriceIndexMarketplace(priceIndexCheapestMarketplace)
            ? priceIndexCheapestMarketplace
            : undefined,
        price_index_signals: priceIndexCounts?.reduce<OpenApiSchemaPriceIndexSignal[]>((acc, signal) => {
            if (isPriceIndexSignal(signal)) {
                acc.push(signal);

                if (signal === 'no_data') {
                    acc.push('bad_data');
                }
            }

            return acc;
        }, []),
        price_index_percent: priceIndexPercent?.value
            ? [{ operator: priceIndexPercent.operator, value: priceIndexPercent.value }]
            : undefined,
        price_index_diff_size: priceIndexDiffSize?.value
            ? [{ operator: priceIndexDiffSize.operator, value: priceIndexDiffSize.value }]
            : undefined,
        minimal_price: isHasMinimalPriceFilterId(minimalPrice) ? minimalPrice : undefined,
    };
}
